// ══════════════════════════════════════════════════════════════
// NOMENCLATURE NGAP — SOURCE DE VÉRITÉ UNIQUE
// Table de correspondance mot-clé → acte cotable.
// Fusion des anciennes tables ACT_PATTERNS (analyse) et NGAP_MAPPINGS (BSI).
// Toute modification de cotation se fait ICI, une seule fois.
// ══════════════════════════════════════════════════════════════

import type { ActeDefinition } from './types';
import { getBaseAMI, getBaseAMX } from '@/lib/tarifs';

export const BASE_AMI = getBaseAMI();
export const BASE_AMX = getBaseAMX();
export const BASE_DI = 10.00;
export const BASE_BSA = 13.00;
export const BASE_BSB = 18.20;
export const BASE_BSC = 28.70;
export const BASE_TMI = 3.15;

export const NOMENCLATURE: ActeDefinition[] = [
  // ═══ A- PRÉLÈVEMENTS ET INJECTIONS ═══

  {
    keywords: ['prise de sang', 'bilan sanguin', 'prelevement veineux', 'prélèvement veineux', 'bilan biologique', 'ponction veineuse', 'prelevement sanguin', 'prélèvement sanguin'],
    code: 'AMI 1,5',
    label: 'Prélèvement sanguin par ponction veineuse',
    coefficient: 1.5,
    categorie: 'Prélèvements',
    article: 'Art. 13B - NGAP - Prélèvements',
    baseAmount: BASE_AMI,
    derogatoire: true,
    jamaisDecote: true,
  },

  {
    keywords: ['insuline', 'injection insuline', 'insuline lente', 'glycemie sous-cut'],
    code: 'AMI 1',
    label: 'Injection sous-cutanée d\'insuline (Art. 5bis)',
    coefficient: 1,
    categorie: 'Injections',
    article: 'Art. 5bis - NGAP — Patient diabétique insulino-traité',
    baseAmount: BASE_AMI,
    article5bis: true,
  },
  {
    keywords: ['heparine', 'héparine', 'lovenox', 'hbpm', 'anticoagulant', 'injection anticoagulant', 'injection sous-cutanée', 'injection sous-cutanee'],
    code: 'AMI 1',
    label: 'Injection sous-cutanée (héparine/anticoagulant)',
    coefficient: 1,
    categorie: 'Injections',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['antibiotique intramusculaire', 'antibiotique im', 'atb im', 'injection intramusculaire', 'injection im', 'intramusculaire'],
    code: 'AMI 1',
    label: 'Injection intramusculaire',
    coefficient: 1,
    categorie: 'Injections',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },
  {
    keywords: ['b12', 'vitamine b12', 'vitamine', 'injection vitamine'],
    code: 'AMI 1',
    label: 'Injection intramusculaire de vitamine B12',
    coefficient: 1,
    categorie: 'Injections',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['injection intraveineuse directe', 'injection iv directe', 'intraveineuse directe', 'iv directe', 'injection intraveineuse isolée', 'injection intraveineuse'],
    code: 'AMI 2',
    label: 'Injection intraveineuse directe isolée',
    coefficient: 2,
    categorie: 'Injections',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['injections intraveineuses en série', 'injections iv en série', 'iv série', 'intraveineuses en série', 'antibiotique intraveineux', 'antibiotique iv', 'atb iv', 'injection intraveineuse antibiotique'],
    code: 'AMI 1,5',
    label: 'Injection intraveineuse (en série)',
    coefficient: 1.5,
    categorie: 'Injections',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },

  // ═══ B- PANSEMENTS ═══

  {
    keywords: ['pansement simple', 'pansement courant', 'refection pansement simple', 'réfection pansement simple'],
    code: 'AMI 2,02',
    label: 'Pansement simple',
    coefficient: 2.02,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansement simple',
    baseAmount: BASE_AMI,
    inclusDansBSI: true,
  },

  {
    keywords: ['pansement tracheotomie', 'pansement trachéotomie', 'tracheotomie pansement', 'trachéotomie pansement', 'soins tracheotomie', 'soins trachéotomie'],
    code: 'AMI 3',
    label: 'Pansement de trachéotomie',
    coefficient: 3,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansement trachéotomie',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['ppnc', 'pansement plaie non chirurgicale', 'plaie non chirurgicale'],
    code: 'AMI 2,02',
    label: 'PPNC — Pansement de plaie(s) non chirurgicale(s)',
    coefficient: 2.02,
    categorie: 'Pansements',
    article: 'Avenant 11 - NGAP — PPNC',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['ppcs', 'pansement de plaie chirurgicale', 'pansement plaie chirurgicale simple', 'pansement de plaie chirurgicale simple', 'plaie chirurgicale simple'],
    code: 'AMI 2,02',
    label: 'PPCS — Pansement de plaie(s) chirurgicale(s) simple(s)',
    coefficient: 2.02,
    categorie: 'Pansements',
    article: 'Avenant 11 - NGAP — PPCS',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['pansement plaies opératoires', 'pansement plaies operatoires', 'abdominoplastie', 'chirurgie mammaire', 'plaies opératoires étendues', 'plaies opératoires multiples'],
    code: 'AMI 3',
    label: 'Pansement de plaies opératoires étendues ou multiples',
    coefficient: 3,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements plaies opératoires étendues',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['pansement postopératoire exérèses varices', 'pansement postoperatoire exereses varices', 'exérèses varices', 'exereses varices', 'ligatures veines perforantes', 'stripping'],
    code: 'AMI 3',
    label: 'Pansement postopératoire d\'exérèses multiples de varices',
    coefficient: 3,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansement exérèses varices',
    baseAmount: BASE_AMI,
  },

  // ═══ C- PANSEMENTS COMPLEXES — AMI 4 (12,60 €) ═══

  {
    keywords: ['bilan initial pansement lourd', 'bilan initial pansements lourds', 'bilan initial pansement complexe', 'bilan initial pansements complexes', 'bilan initial plaie complexe', 'bilan initial plaies complexes', 'evaluation initiale pansement lourd', 'évaluation initiale pansement lourd', 'evaluation initiale pansement complexe', 'évaluation initiale pansement complexe'],
    code: 'AMI 11',
    label: 'Bilan initial pour pansements lourds et complexes (AMI 11)',
    coefficient: 11,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Bilan initial pansements lourds et complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
    noMCI: true,
  },

  {
    keywords: ['brûlure radiothérapie', 'brulure radiotherapie', 'pansement brûlure radiothérapie'],
    code: 'AMI 4',
    label: 'Pansement de brûlure suite à radiothérapie (>2%)',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['brûlure étendue', 'brulure etendue', 'plaie chimique', 'plaie thermique', 'brûlure chimique', 'brulure chimique', 'brûlure thermique', 'brulure thermique'],
    code: 'AMI 4',
    label: 'Pansement de brûlure étendue / plaie chimique ou thermique (>5%)',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['pansement méchage', 'pansement mechage', 'méchage', 'mechage', 'irrigation plaie'],
    code: 'AMI 4',
    label: 'Pansement de méchage ou irrigation',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['ulcère étendu', 'ulcere etendu', 'greffe cutanée', 'greffe cutanee', 'ulcère greffe', 'ulcere greffe'],
    code: 'AMI 4',
    label: 'Pansement d\'ulcère étendu ou de greffe cutanée (>60 cm²)',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
    cumulableAvecAnalgesie: true,
  },

  {
    keywords: ['amputation détersion', 'amputation detersion', 'amputation épluchage', 'amputation epluchage', 'amputation régularisation', 'amputation regularisation', 'pansement amputation'],
    code: 'AMI 4',
    label: 'Pansement d\'amputation nécessitant détersion, épluchage et régularisation',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['fistule digestive', 'pansement fistule digestive', 'fistule'],
    code: 'AMI 4',
    label: 'Pansement de fistule digestive',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['perte de substance', 'perte substance', 'traumatique profonde', 'néoplasique profonde', 'neoplasique profonde', 'lésions profondes', 'lesions profondes', 'aponévrotique', 'aponevrotique'],
    code: 'AMI 4',
    label: 'Pansement pour perte de substance traumatique ou néoplasique profonde',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['escarre profonde', 'escarre étendue', 'escarre etendue', 'escarre muscle', 'escarre tendon'],
    code: 'AMI 4',
    label: 'Pansement d\'escarre profonde et étendue atteignant muscles ou tendons',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
    cumulableAvecAnalgesie: true,
  },

  {
    keywords: ['ostéosynthèse extériorisé', 'osteosynthese exteriorise', 'matériel ostéosynthèse', 'materiel osteosynthese', 'fixateur externe', 'pansement chirurgical ostéosynthèse'],
    code: 'AMI 4',
    label: 'Pansement chirurgical avec matériel d\'ostéosynthèse extériorisé',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansements complexes',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  // ═══ D- PERFUSIONS ═══

  {
    keywords: ['perfusion courte cancer', 'perfusion courte immunodéprimé', 'perfusion courte cancéreux', 'perfusion courte chimio'],
    code: 'AMI 10',
    label: 'Perfusion courte (≤1h) — patient cancéreux/immunodéprimé',
    coefficient: 10,
    categorie: 'Perfusions',
    article: 'Art. 11B - NGAP — Perfusions',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['perfusion courte', 'perfusion 1h', 'perfusion ≤1h', 'perfusion sous surveillance', 'perfusion antibiotique', 'perfusion moins d\'une heure'],
    code: 'AMI 9',
    label: 'Perfusion courte (≤1h) sous surveillance continue',
    coefficient: 9,
    categorie: 'Perfusions',
    article: 'Art. 11B - NGAP — Perfusions',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['heure supplémentaire', 'heure supplementaire', 'heure supplémentaire perfusion courte', 'heure supplementaire perfusion courte', 'heure de surveillance supplémentaire', 'heure de surveillance supplementaire', 'ami 6', 'prolongation surveillance perfusion'],
    code: 'AMI 6',
    label: 'Heure supplémentaire de surveillance continue de perfusion (AMI 6)',
    coefficient: 6,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 6 : heure supplémentaire de surveillance continue (perfusion courte AMI 9/10)',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['perfusion longue cancer', 'perfusion longue immunodéprimé', 'perfusion longue cancéreux', 'perfusion longue chimio'],
    code: 'AMI 15',
    label: 'Perfusion longue (>1h) — patient cancéreux/immunodéprimé',
    coefficient: 15,
    categorie: 'Perfusions',
    article: 'Art. 11B - NGAP — Perfusions',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['perfusion longue', 'perfusion >1h', 'perfusion plusieurs heures', 'perfusion continue', 'perfusion hydration'],
    code: 'AMI 14',
    label: 'Perfusion longue (>1h) avec surveillance organisée',
    coefficient: 14,
    categorie: 'Perfusions',
    article: 'Art. 11B - NGAP — Perfusions',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['organisation surveillance perfusion longue', 'surveillance journalière perfusion', 'ami 4 surveillance perfusion', 'surveillance perfusion longue hors pose'],
    code: 'AMI 4',
    label: 'Organisation de la surveillance d\'une perfusion longue (AMI 4 — CIR-9/2025)',
    coefficient: 4,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 4 : organisation surveillance perfusion longue (hors jour pose et retrait)',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['pose de perfusion', 'pose perfusion', 'perfusion pose'],
    code: 'AMI 4',
    label: 'Organisation de la surveillance d\'une perfusion longue (AMI 4 — CIR-9/2025)',
    coefficient: 4,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 4 : organisation surveillance perfusion longue (hors jour pose et retrait)',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['surveillance perfusion', 'surveiller perfusion'],
    code: 'AMI 2',
    label: 'Surveillance de perfusion',
    coefficient: 2,
    categorie: 'Perfusions',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['dépose perfusion', 'depose perfusion', 'ablation perfusion', 'retrait perfusion', 'arrêt définitif perfusion', 'arret definitif perfusion', 'arrêt perfusion plus de 24h', 'arret perfusion plus de 24h', 'retrait définitif perfusion', 'retrait definitif perfusion', 'retrait perfusion plus de 24h', 'ablation perfusion plus de 24h', 'débranchement définitif', 'debranchement definitif'],
    code: 'AMI 5',
    label: 'Arrêt définitif de perfusion > 24h (AMI 5 — CIR-9/2025)',
    coefficient: 5,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 5 : arrêt définitif de perfusion > 24h',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['retrait midline', 'dépose midline', 'depose midline', 'ablation midline', 'retrait picc line', 'retrait picc', 'dépose picc line', 'depose picc line', 'ablation picc line', 'ablation picc', 'retrait définitif midline', 'retrait definitif midline', 'retrait définitif picc', 'retrait definitif picc', 'dépose définitive midline', 'depose definitive midline', 'dépose définitive picc', 'depose definitive picc', 'changement valve midline', 'changement valve picc', 'retrait dispositif type midline', 'retrait dispositif type picc'],
    code: 'AMI 5',
    label: 'Retrait définitif Midline / Picc Line (AMI 5 — CIR-9/2025)',
    coefficient: 5,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 5 : retrait définitif Midline/Picc Line (pansement + valve inclus)',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  {
    keywords: ['retrait perfusion moins de 24h', 'ablation perfusion moins de 24h', 'dépose perfusion moins de 24h', 'depose perfusion moins de 24h', 'deuxième perfusion longue', 'deuxieme perfusion longue', 'seconde perfusion longue', 'ami 4.1', 'ami 4,1'],
    code: 'AMI 4,1',
    label: 'Retrait perfusion < 24h ou 2e perfusion longue (AMI 4,1 — CIR-9/2025)',
    coefficient: 4.1,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — Arrêt perfusion < 24h en AMI 4,1 (sauf soin = perfusion unique) ou 2e perfusion longue',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['changement de flacon', 'changement flacon perfusion'],
    code: 'AMI 4,1',
    label: 'Changement de flacon de perfusion (AMI 4,1 — CIR-9/2025)',
    coefficient: 4.1,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 4,1 : changement de flacon sur prescription médicale',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['surveillance du débit', 'surveillance du debit', 'surveillance débit', 'surveillance debit'],
    code: 'AMI 4,1',
    label: 'Surveillance du débit de perfusion (AMI 4,1 — CIR-9/2025)',
    coefficient: 4.1,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 4,1 : surveillance du débit sur prescription médicale',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['branchement en y', 'branchement en Y'],
    code: 'AMI 4,1',
    label: 'Branchement en Y de perfusion (AMI 4,1 — CIR-9/2025)',
    coefficient: 4.1,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 4,1 : branchement en Y (2e AMI 4,1 à demi-tarif)',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['déplacement de la perfusion', 'deplacement de la perfusion', 'déplacement perfusion', 'deplacement perfusion'],
    code: 'AMI 4,1',
    label: 'Déplacement de la perfusion (AMI 4,1 — CIR-9/2025)',
    coefficient: 4.1,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 4,1 : déplacement de la perfusion sur prescription médicale',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['appel suite à complication', 'appel suite a complication', 'complication sur perfusion', 'appel complication perfusion'],
    code: 'AMI 4,1',
    label: 'Appel suite à une complication sur perfusion (AMI 4,1 — CIR-9/2025)',
    coefficient: 4.1,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 4,1 : appel suite à une complication (hors pose initiale AMI 14/15)',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },
  {
    keywords: ['débranchement temporaire', 'debranchement temporaire', 'débranchement perfusion moins de 24h', 'debranchement perfusion moins de 24h'],
    code: 'AMI 4,1',
    label: 'Débranchement temporaire de perfusion < 24h (AMI 4,1 — CIR-9/2025)',
    coefficient: 4.1,
    categorie: 'Perfusions',
    article: 'CIR-9/2025 — AMI 4,1 : débranchement temporaire < 24h',
    baseAmount: BASE_AMI,
    derogatoire: true,
  },

  // ═══ E- SONDAGES ═══

  {
    keywords: ['pose sonde urinaire femme', 'pose sonde urinaire', 'sondage urinaire femme', 'sonde urinaire femme'],
    code: 'AMI 3',
    label: 'Pose de sonde urinaire chez la femme',
    coefficient: 3,
    categorie: 'Sondages',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['catheterisme uretral', 'cathétérisme urétral', 'sonde urinaire homme', 'catheterisme homme', 'cathétérisme homme', 'sondage homme'],
    code: 'AMI 4',
    label: 'Cathétérisme urétral chez l\'homme',
    coefficient: 4,
    categorie: 'Sondages',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['retrait de sonde urinaire', 'ablation de sonde urinaire', 'ablation sonde urinaire', 'retrait sonde urinaire', 'ablation sonde', 'retrait sonde'],
    code: 'AMI 2',
    label: 'Ablation de sonde urinaire',
    coefficient: 2,
    categorie: 'Sondages',
    article: 'Art. 7 NGAP — Retrait sonde urinaire (cumul postopératoire)',
    baseAmount: BASE_AMI,
    article7: true,
  },

  // ═══ F- SOINS DE NURSING (ex-AIS) — INCLUS DANS LE FORFAIT BSI ═══

  {
    keywords: ['toilette', 'aide toilette', 'ais 3', 'ais3', 'soins hygiene', 'soins hygiène', 'soins de confort'],
    code: 'N/A',
    label: 'Aide à la toilette — incluse dans le forfait BSI',
    coefficient: 0,
    categorie: 'Soins infirmiers',
    article: 'Avenant 11 - NGAP — AIS supprimée, incluse dans le forfait BSI',
    baseAmount: 0,
    inclusDansBSI: true,
  },
  {
    keywords: ['change', 'changement protection', 'change protection', 'incontinence', 'incontinance', 'nursing', 'change nursing', 'change patient', 'protection change'],
    code: 'N/A',
    label: 'Change / protection du patient — inclus dans le forfait BSI',
    coefficient: 0,
    categorie: 'Soins infirmiers',
    article: 'Avenant 11 - NGAP — AIS supprimée, incluse dans le forfait BSI',
    baseAmount: 0,
    inclusDansBSI: true,
  },
  {
    keywords: ['lever', 'coucher', 'aide au lever', 'aide coucher', 'transfert'],
    code: 'N/A',
    label: 'Aide au lever / coucher — incluse dans le forfait BSI',
    coefficient: 0,
    categorie: 'Soins infirmiers',
    article: 'Avenant 11 - NGAP — AIS supprimée, incluse dans le forfait BSI',
    baseAmount: 0,
    inclusDansBSI: true,
  },
  {
    keywords: ['alimentation', 'aide repas', 'repas'],
    code: 'N/A',
    label: 'Aide à l\'alimentation — incluse dans le forfait BSI',
    coefficient: 0,
    categorie: 'Soins infirmiers',
    article: 'Avenant 11 - NGAP — AIS supprimée, incluse dans le forfait BSI',
    baseAmount: 0,
    inclusDansBSI: true,
  },

  // ═══ G- SURVEILLANCE ═══

  {
    keywords: ['tension', 'tension arterielle', 'tension artérielle', 'ta', 'pression artérielle', 'pression arterielle', 'pouls', 'temperature', 'température', 'constantes', 'prise de constantes'],
    code: 'AMI 1',
    label: 'Surveillance tension artérielle / pouls (Art. 10)',
    coefficient: 1,
    categorie: 'Surveillance',
    article: 'Art. 10 - NGAP — Surveillance de traitement (limité à 15 jours)',
    baseAmount: BASE_AMI,
  },
  {
    keywords: ['administration de traitement', 'administration traitement', 'administration medicament', 'administration médicament', 'administration therapeutique', 'administration thérapeutique', 'distribution medicament', 'distribution médicament', 'prise medicament', 'prise médicament', 'donner medicament', 'donner médicament', 'traitement oral', 'traitement per os'],
    code: 'AMI 1,2',
    label: 'Administration de traitement (Art. 10)',
    coefficient: 1.2,
    categorie: 'Surveillance',
    article: 'Art. 10 - NGAP — Administration de traitement (limité à 15 jours)',
    baseAmount: BASE_AMI,
    inclusDansBSI: true,
  },
  {
    keywords: ['saturation', 'spo2', 'oxygene', 'oxygène'],
    code: 'AMI 1',
    label: 'Mesure de la saturation en oxygène',
    coefficient: 1,
    categorie: 'Surveillance',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },
  {
    keywords: ['surveillance glycemique', 'surveillance glycémique', 'controle glycemie', 'contrôle glycémie', 'dextro'],
    code: 'AMI 1',
    label: 'Surveillance glycémique (dextro)',
    coefficient: 1,
    categorie: 'Surveillance',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },

  // ═══ H- ARTICLE 5 BIS — PATIENT DIABÉTIQUE INSULINO-TRAITÉ ═══

  {
    keywords: ['surveillance insuline', 'adaptation insuline', 'surveillance diabétique', 'surveillance diabetique', 'surveillance patient diabétique', 'surveillance patient diabetique', 'observation diabetique', 'observation diabétique', 'suivi diabetique', 'suivi diabétique', 'surveillance et observation patient diabetique', 'surveillance et observation patient diabétique'],
    code: 'AMI 1',
    label: 'Surveillance et adaptation des doses d\'insuline (Art. 5bis)',
    coefficient: 1,
    categorie: 'Injections',
    article: 'Art. 5bis - NGAP — Patient diabétique insulino-traité',
    baseAmount: BASE_AMI,
    article5bis: true,
  },
  {
    keywords: ['surveillance clinique hebdomadaire', 'surveillance hebdomadaire', 'séance hebdomadaire surveillance', 'seance hebdomadaire surveillance'],
    code: 'AMI 4',
    label: 'Séance hebdo. de surveillance clinique (>75 ans, 30 min) — AMI 4 (Art. 5bis)',
    coefficient: 4,
    categorie: 'Surveillance',
    article: 'Art. 5bis - NGAP — Surveillance clinique patient >75 ans',
    baseAmount: BASE_AMI,
    article5bis: true,
  },
  {
    keywords: ['pansement lourd diabetique', 'pansement lourd diabétique', 'pansement lourd insulino', 'pansement diabetique complexe', 'patient diabétique insulino-traité avec détersion'],
    code: 'AMI 4',
    label: 'Pansement lourd et complexe patient insulino-traité (Art. 5bis)',
    coefficient: 4,
    categorie: 'Pansements',
    article: 'Art. 5bis - NGAP — Pansement lourd patient insulino-traité',
    baseAmount: BASE_AMI,
    derogatoire: true,
    article5bis: true,
  },
  {
    keywords: ['analgesie topique', 'analgésie topique', 'anesthesie topique', 'anesthésie topique', 'emla'],
    code: 'AMI 1,1',
    label: 'Analgésie topique préalable à un pansement d\'ulcère ou d\'escarre (AMI 1,1)',
    coefficient: 1.1,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Analgésie topique (dérogation cumul)',
    baseAmount: BASE_AMI,
    derogatoire: true,
    analgesieTopique: true,
  },

  // ═══ I- ARTICLE 5 TER — SURVEILLANCE POST-HOSPITALISATION (PRADO) ═══

  {
    keywords: ['surveillance post-hospitalisation', 'surveillance post hospitalisation', 'suivi post-hospitalisation', 'surveillance renforcée domicile', 'surveillance renforcee domicile', 'suivi retour hospitalisation', 'prado', 'programme retour à domicile', 'programme retour a domicile', 'retour à domicile', 'retour a domicile'],
    code: 'AMI 5,8',
    label: 'Séance de surveillance renforcée post-hospitalisation (Art. 5ter)',
    coefficient: 5.8,
    categorie: 'Surveillance',
    article: 'Art. 5ter - NGAP — Surveillance post-hospitalisation (max 15 séances)',
    baseAmount: BASE_AMI,
    article5ter: true,
    derogatoire: true,
  },

  // ═══ J- ARTICLE 6 — DIALYSE PÉRITONÉALE ═══

  {
    keywords: ['dialyse peritoneale', 'dialyse péritonéale', 'dialyse a domicile', 'dialyse à domicile', 'dpca', 'dp'],
    code: 'AMI 4',
    label: 'Séance de dialyse péritonéale à domicile (Art. 6)',
    coefficient: 4,
    categorie: 'Dialyse',
    article: 'Art. 6 - NGAP — Dialyse péritonéale (max 4 séances/jour)',
    baseAmount: BASE_AMI,
    article6: true,
  },

  // ═══ K- ARTICLE 7 — SOINS POSTOPÉRATOIRES ═══

  {
    keywords: ['surveillance clinique postopératoire', 'surveillance clinique postoperatoire', 'surveillance clinique post-opératoire', 'surveillance postoperatoire', 'surveillance postopératoire', 'surveillance post-opératoire', 'suivi postoperatoire', 'suivi postopératoire', 'raac', 'chirurgie ambulatoire'],
    code: 'AMI 3,9',
    label: 'Séance de surveillance clinique postopératoire (Art. 7)',
    coefficient: 3.9,
    categorie: 'Surveillance',
    article: 'Art. 7 - NGAP — Soins postopératoires (RAAC)',
    baseAmount: BASE_AMI,
    article7: true,
  },
  {
    keywords: ['drain de redon', 'drain redon', 'surveillance drain', 'retrait drain', 'ablation drain', 'changement flacon'],
    code: 'AMI 2,8',
    label: 'Surveillance/retrait de drain de Redon (Art. 7)',
    coefficient: 2.8,
    categorie: 'Sondages',
    article: 'Art. 7 - NGAP — Drain de Redon',
    baseAmount: BASE_AMI,
    article7: true,
  },
  {
    keywords: ['catheter perinerveux', 'cathéter périnerveux', 'catheter analgésie', 'cathéter analgésie', 'kat kat', 'périnerveux'],
    code: 'AMI 4,2',
    label: 'Séance cathéter périnerveux pour analgésie postopératoire (Art. 7)',
    coefficient: 4.2,
    categorie: 'Perfusions',
    article: 'Art. 7 - NGAP — Cathéter périnerveux',
    baseAmount: BASE_AMI,
    article7: true,
  },

  // ═══ L- AUTRES COTATIONS FRÉQUENTES ═══

  {
    keywords: ['vaccin avec prescription', 'vaccination avec prescription', 'vaccin prescrit', 'vaccination prescrite'],
    code: 'AMI 2,4',
    label: 'Vaccin avec prescription',
    coefficient: 2.4,
    categorie: 'Injections',
    article: 'Art. 11B - NGAP — Vaccination',
    baseAmount: BASE_AMI,
  },
  {
    keywords: ['vaccin sans prescription', 'vaccination sans prescription', 'vaccin obligatoire', 'vaccination obligatoire'],
    code: 'AMI 3,05',
    label: 'Vaccin à prescription obligatoire sans prescription préalable',
    coefficient: 3.05,
    categorie: 'Injections',
    article: 'Art. 11B - NGAP — Vaccination',
    baseAmount: BASE_AMI,
  },
  {
    keywords: ['vaccin', 'vaccination'],
    code: 'AMI 2,4',
    label: 'Vaccination (vérifier si avec ou sans prescription)',
    coefficient: 2.4,
    categorie: 'Injections',
    article: 'Art. 11B - NGAP — Vaccination',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['aerosol', 'aérosol', 'nebulisation', 'nébulisation', 'inhalation'],
    code: 'AMI 1,5',
    label: 'Séance d\'aérosol',
    coefficient: 1.5,
    categorie: 'Soins spécifiques',
    article: 'Art. 11B - NGAP — Aérosol',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['collyre', 'instillation collyre', 'goutte oeil', 'goutte œil', 'soin oculaire'],
    code: 'AMI 1,2',
    label: 'Instillation de collyre',
    coefficient: 1.2,
    categorie: 'Soins spécifiques',
    article: 'Art. 11B - NGAP',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['ecbu', 'examen cytobacteriologique', 'examen cytobactériologique', 'bandelette urinaire', 'prelevement urinaire', 'prélèvement urinaire'],
    code: 'AMI 1,5',
    label: 'ECBU / Prélèvement urinaire',
    coefficient: 1.5,
    categorie: 'Prélèvements',
    article: 'Art. 13B - NGAP — ECBU',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['inr', 'prelevement capillaire', 'prélèvement capillaire', 'coagulation'],
    code: 'AMI 1',
    label: 'Prélèvement capillaire INR',
    coefficient: 1,
    categorie: 'Prélèvements',
    article: 'Art. 13B - NGAP — Prélèvements',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['glycemie capillaire', 'glycémie capillaire', 'dextro glycemie'],
    code: 'AMI 1',
    label: 'Glycémie capillaire (dextro)',
    coefficient: 1,
    categorie: 'Prélèvements',
    article: 'Art. 13B - NGAP — Prélèvements',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['stomie digestive', 'stomie', 'pansement stomie', 'soins stomie', 'colostomie', 'ileostomie', 'iléostomie'],
    code: 'AMI 2',
    label: 'Soins de stomie digestive avec pansement',
    coefficient: 2,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Stomie',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['lavement evacuateur', 'lavement évacuateur', 'lavement', 'normacol'],
    code: 'AMI 3',
    label: 'Lavement évacuateur',
    coefficient: 3,
    categorie: 'Soins spécifiques',
    article: 'Art. 11B - NGAP — Lavement',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['pose sonde nasogastrique', 'pose sonde naso gastrique', 'pose sonde gastrique', 'pose sng', 'sonde nasogastrique', 'alimentation enterale', 'alimentation entérale', 'nutrition entérale pompe', 'nutrition entérale', 'nutrition enterale', 'sondage alimentation', 'gastrostomie', 'gavage'],
    code: 'AMI 3',
    label: 'Alimentation entérale — pose de sonde ou séance de gavage',
    coefficient: 3,
    categorie: 'Soins spécifiques',
    article: 'Art. 11B - NGAP — Alimentation entérale',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['bandes de contention', 'bande de contention', 'contention veineuse', 'contention elastique', 'contention élastique'],
    code: 'AMI 2',
    label: 'Pose de bandes de contention',
    coefficient: 2,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Contention',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['pansement avec compression', 'pansement compressif', 'bande compressive', 'contention forte', 'compression pansement', 'compression veineuse pansement'],
    code: 'AMI 5,1',
    label: 'Pansement avec compression (AMI 5,1)',
    coefficient: 5.1,
    categorie: 'Pansements',
    article: 'Art. 11B - NGAP — Pansement avec compression',
    baseAmount: BASE_AMI,
    derogatoire: true,
    cumulableAvecAnalgesie: true,
  },

  {
    keywords: ['capteur glycemie', 'capteur glycémie', 'capteur glucose', 'freestyle', 'pose capteur'],
    code: 'AMI 1',
    label: 'Pose de capteur de glycémie',
    coefficient: 1,
    categorie: 'Soins spécifiques',
    article: 'Art. 11B - NGAP — Capteur glycémie',
    baseAmount: BASE_AMI,
  },

  {
    keywords: ['pcr', 'test pcr', 'prelevement pcr', 'prélèvement pcr', 'covid', 'test covid'],
    code: 'AMI 1,5',
    label: 'Prélèvement PCR',
    coefficient: 1.5,
    categorie: 'Prélèvements',
    article: 'Art. 13B - NGAP — Prélèvement PCR',
    baseAmount: BASE_AMI,
  },

  // ═══ M- TÉLÉSOIN / ACTES À DISTANCE ═══

  {
    keywords: ['suivi de pansement', 'suivi pansement', 'suivi de pansement à distance', 'suivi pansement distance', 'tmi 1,6', 'tmi 1.6'],
    code: 'TMI 1,6',
    label: 'Suivi de pansement à distance (TMI 1,6)',
    coefficient: 1.6,
    categorie: 'Télésoin',
    article: 'Avenant n°9 - Convention nationale IDEL - Télésoin (suivi de pansement à distance)',
    baseAmount: BASE_TMI,
  },
  {
    keywords: ['accompagnement teleconsultation domicile hors soin', 'tld', 'teleconsultation domicile hors soin', 'téléconsultation domicile hors soin'],
    code: 'TLD',
    label: 'Accompagnement téléconsultation à domicile, hors soin prévu',
    coefficient: 1,
    categorie: 'Télésoin',
    article: 'Avenant n°9 - Convention nationale IDEL - Télésoin',
    baseAmount: 15.00,
  },
  {
    keywords: ['accompagnement teleconsultation lieu dedie', 'accompagnement téléconsultation lieu dédié', 'tll'],
    code: 'TLL',
    label: 'Accompagnement téléconsultation en lieu dédié',
    coefficient: 1,
    categorie: 'Télésoin',
    article: 'Avenant n°9 - Convention nationale IDEL - Télésoin',
    baseAmount: 12.00,
  },
  {
    keywords: ['accompagnement teleconsultation', 'accompagnement téléconsultation', 'tls'],
    code: 'TLS',
    label: 'Accompagnement téléconsultation (soin déjà prévu)',
    coefficient: 1,
    categorie: 'Télésoin',
    article: 'Avenant n°9 - Convention nationale IDEL - Télésoin',
    baseAmount: 10.00,
  },
  {
    keywords: ['teleconsultation', 'téléconsultation', 'telesoin', 'télésoin', 'visio', 'consultation a distance', 'consultation à distance'],
    code: 'TMI',
    label: 'Acte réalisé à distance (TMI)',
    coefficient: 1,
    categorie: 'Télésoin',
    article: 'Avenant n°9 - Convention nationale IDEL - Télésoin',
    baseAmount: BASE_TMI,
  },

  // ═══ N- DÉMARCHE DE SOINS (DI) — BSI ═══

  {
    keywords: ['bsi initial', 'première évaluation', 'premier bsi', 'bsi première', 'bsi 1ere', 'bsi 1ère', 'evaluation initiale', 'évaluation initiale'],
    code: 'DI 2,5',
    label: 'BSI initial — première évaluation du patient (25,00 €)',
    coefficient: 2.5,
    categorie: 'Démarche de soins',
    article: 'Art. 12 - NGAP — BSI initial (DI 2,5)',
    baseAmount: BASE_DI,
    isDaily: true,
  },
  {
    keywords: ['bsi renouvellement', 'renouvellement bsi', 'bsi annuel', 'renouveler bsi'],
    code: 'DI 1,2',
    label: 'BSI de renouvellement — après un an, nouvelle prescription (12,00 €)',
    coefficient: 1.2,
    categorie: 'Démarche de soins',
    article: 'Art. 12 - NGAP — BSI de renouvellement (DI 1,2)',
    baseAmount: BASE_DI,
    isDaily: true,
  },
  {
    keywords: ['bsi intermédiaire', 'évolution bsi', 'bsi évolution', 'bsi suite', 'bsi aggrav'],
    code: 'DI 1,2',
    label: 'BSI intermédiaire — évolution clinique, max 2/an (12,00 €)',
    coefficient: 1.2,
    categorie: 'Démarche de soins',
    article: 'Art. 12 - NGAP — BSI intermédiaire (DI 1,2, max 2/an)',
    baseAmount: BASE_DI,
    isDaily: true,
  },
  {
    keywords: ['demarche de soins', 'démarche de soins', 'bsi', 'bilan de soins infirmiers', 'plan de soins'],
    code: 'DI 1',
    label: 'Démarche de soins infirmiers / BSI (type non précisé)',
    coefficient: 1,
    categorie: 'Démarche de soins',
    article: 'Art. 12 - NGAP - Démarche de soins infirmiers',
    baseAmount: BASE_DI,
    isDaily: true,
  },

  // ═══ O- FORFAITS JOURNALIERS DE DÉPENDANCE ═══

  {
    keywords: ['bsa', 'dépendance légère', 'dependance legere', 'forfait léger', 'forfait leger', 'gir 4', 'gir4'],
    code: 'BSA',
    label: 'Forfait journalier dépendance légère — GIR 4 (13,00 €/j)',
    coefficient: 1,
    categorie: 'Forfait dépendance',
    article: 'BSI — Forfait dépendance léger (GIR 4)',
    baseAmount: BASE_BSA,
    isDaily: true,
  },
  {
    keywords: ['bsb', 'dépendance intermédiaire', 'dependance intermediaire', 'forfait intermédiaire', 'gir 3', 'gir3'],
    code: 'BSB',
    label: 'Forfait journalier dépendance intermédiaire — GIR 3 (18,20 €/j)',
    coefficient: 1,
    categorie: 'Forfait dépendance',
    article: 'BSI — Forfait dépendance intermédiaire (GIR 3)',
    baseAmount: BASE_BSB,
    isDaily: true,
  },
  {
    keywords: ['bsc', 'dépendance lourde', 'dependance lourde', 'forfait lourd', 'gir 1', 'gir1', 'gir 2', 'gir2', 'grabataire'],
    code: 'BSC',
    label: 'Forfait journalier dépendance lourde — GIR 1-2 (28,70 €/j)',
    coefficient: 1,
    categorie: 'Forfait dépendance',
    article: 'BSI — Forfait dépendance lourde (GIR 1-2)',
    baseAmount: BASE_BSC,
    isDaily: true,
  },

  // ═══ P- AMX (générique, fallback) ═══
  {
    keywords: ['amx'],
    code: 'AMX',
    label: 'Acte technique dans le cadre dépendance (AMX)',
    coefficient: 1,
    categorie: 'Injections',
    article: 'Art. 11B - NGAP — Actes dans le cadre BSI',
    baseAmount: BASE_AMX,
  },
];

/**
 * Détecte l'acte NGAP correspondant à un mot-clé.
 * Priorité au match de mot-clé le PLUS LONG (évite les faux positifs
 * comme « ta » qui matche dans « consulTAion »).
 */
export function detecterActe(keyword: string): ActeDefinition | null {
  const kwLower = keyword.toLowerCase();
  let bestMatch: ActeDefinition | null = null;
  let bestLength = 0;

  for (const definition of NOMENCLATURE) {
    for (const pk of definition.keywords) {
      if (kwLower.includes(pk) && pk.length > bestLength) {
        bestMatch = definition;
        bestLength = pk.length;
      }
    }
  }

  return bestMatch;
}

/**
 * Normalise un code AMX en AMI pour les comparaisons internes.
 */
export function normCode(code: string): string {
  return code.replace(/^AMX/, 'AMI');
}